import { useMemo, useState } from 'react';
import { addDays, format, parseISO, startOfDay, isSameDay } from 'date-fns';
import dayjs, { type Dayjs } from 'dayjs';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Stack,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DatePicker } from '@mui/x-date-pickers';

import AppPage from '@/shared/components/PageComponents/AppPage';
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle';
import { useGames } from '@/features/games/hooks/gameQueries';
import useSeasonIdForDate from '@/features/games/hooks/useSeasonIdForDate';

import { WscGameCard } from './WscGameCard';
import PageHeader from '@/shared/components/PageHeader';

/**
 * Single-day view: shows only the games scheduled for `selectedDate`. Default
 * is today. Operators switch days via prev/next arrows or by opening the
 * calendar picker on the date field (for arbitrary jumps).
 *
 * `useGames` is called with the selected date as its anchor; the page then
 * filters client-side to *exactly* that calendar day so any drift in the
 * server's window doesn't leak adjacent days into the view.
 */
export default function WscStreamsPage() {
  const title = 'SRT Streams';
  useDocumentTitle(title);

  const [selectedDate, setSelectedDate] = useState<Date>(() => startOfDay(new Date()));

  // The games API requires a season, and this view only knows a day — so the
  // season is resolved from the selected date rather than pinned, keeping the
  // date picker free to jump into earlier seasons.
  const { seasonId, isLoading: isSeasonLoading } = useSeasonIdForDate(selectedDate);

  // Show all game states for the day, not just scheduled — operators want to
  // see what streams ran (and their outcomes) for live / recorded games too.
  // Non-scheduled games render in read-only mode (handled in WscGameCard);
  // this page just feeds the data through.
  const { data, isLoading: isGamesLoading, error } = useGames({
    seasonId,
    date: selectedDate,
    streamTypes: ['live', 'recorded', 'scheduled'],
    limit: 50,
    upcomingLimit: 50,
  });

  // The season lookup gates the games query, so both have to settle before the
  // page is entitled to say there are no games that day.
  const isLoading = isGamesLoading || isSeasonLoading;

  const todayGames = useMemo(() => {
    const games = data?.games ?? [];
    return games
      .filter((g: any) => {
        if (!g.startTimestamp) return false;
        const ts = typeof g.startTimestamp === 'string' ? parseISO(g.startTimestamp) : new Date(g.startTimestamp);
        return isSameDay(ts, selectedDate);
      })
      .sort((a: any, b: any) => {
        const ta = typeof a.startTimestamp === 'string' ? parseISO(a.startTimestamp).getTime() : new Date(a.startTimestamp).getTime();
        const tb = typeof b.startTimestamp === 'string' ? parseISO(b.startTimestamp).getTime() : new Date(b.startTimestamp).getTime();
        return ta - tb;
      });
  }, [data?.games, selectedDate]);

  const isToday = isSameDay(selectedDate, new Date());
  const handlePrevDay = () => setSelectedDate((d) => addDays(d, -1));
  const handleNextDay = () => setSelectedDate((d) => addDays(d, 1));
  const handleJumpToToday = () => setSelectedDate(startOfDay(new Date()));

  return (
    <AppPage title={title} requiredPermissions={['menu:games:wsc-streams']}>
      <Container maxWidth="xl" sx={{ marginTop: 4, marginBottom: 4 }}>
        <PageHeader title="Manage SRT Streams per game" />

        {/* Day navigator */}
        <Stack direction="row" sx={{ gap: 2, mb: 3, alignItems: 'center', justifyContent: 'center' }}>
          <IconButton onClick={handlePrevDay} aria-label="Previous day" size="large">
            <ChevronLeftIcon />
          </IconButton>

          <Box sx={{ minWidth: 200, textAlign: 'center' }}>
            <DatePicker<Dayjs>
              value={dayjs(selectedDate)}
              onChange={(newDate) => {
                if (newDate && newDate.isValid()) {
                  setSelectedDate(startOfDay(newDate.toDate()));
                }
              }}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  size: 'small',
                  fullWidth: true,
                  onClick: (e) => e.preventDefault(),
                  sx: { cursor: 'pointer' },
                },
              }}
            />
          </Box>

          <IconButton onClick={handleNextDay} aria-label="Next day" size="large">
            <ChevronRightIcon />
          </IconButton>

          {!isToday && (
            <Button variant="outlined" size="small" onClick={handleJumpToToday} sx={{ ml: 1 }}>
              Today
            </Button>
          )}
        </Stack>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ my: 2 }}>
            Failed to load games: {(error as any)?.message ?? 'unknown error'}
          </Alert>
        )}

        {!isLoading && !error && todayGames.length === 0 && (
          <Alert severity="info">No games scheduled for {format(selectedDate, 'MMM dd, yyyy')}</Alert>
        )}

        {!isLoading && !error && todayGames.length > 0 && (
          <Box>
            {todayGames.map((game: any) => (
              <WscGameCard key={game.id ?? game.uniqueId} game={game} />
            ))}
          </Box>
        )}
      </Container>
    </AppPage>
  );
}
